/**
 * La politique de confidentialité, à son adresse publique.
 *
 * ## Pourquoi une page du site plutôt qu'un lien vers le document
 *
 * Le texte existe aussi en document partagé, et ce lien reste offert en bas de
 * page. Mais c'est CETTE page que les consentements citent : chaque
 * autorisation d'image enregistre le numéro de version affiché au moment de la
 * réponse, et une version doit pouvoir être retrouvée telle quelle.
 *
 * Trois choses qu'un document partagé ne sait pas faire, et dont on a besoin :
 * il n'a pas d'adresse stable qu'un moteur indexe, il se modifie sans laisser
 * de version datée derrière lui, et il oblige la famille à sortir du site — au
 * moment précis où on lui demande de lire avant de consentir.
 *
 * ## Le texte n'est pas paraphrasé
 *
 * Il est reproduit tel qu'Éclosion l'a rédigé. La mise en forme est à nous ;
 * les mots ne le sont pas. Une politique de confidentialité que le code
 * reformule est une politique que personne n'a validée.
 */

import { Link } from 'react-router-dom';
import { ExternalLink, Mail } from 'lucide-react';
import { useEnTete } from '../../lib/seo/enTete';
import {
  CHEMIN_POLITIQUE,
  COURRIEL_VIE_PRIVEE,
  DOCUMENT_SOURCE,
  MAJ_POLITIQUE,
  VERSION_POLITIQUE,
} from '../../lib/confidentialite';

/** Une section numérotée du document, avec son ancre. */
const Section = ({
  id,
  titre,
  children,
}: {
  id: string;
  titre: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-xl font-bold text-gray-900 mb-3 md:text-2xl">{titre}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

/** Une liste à puces, celle du document. */
const Puces = ({ items }: { items: React.ReactNode[] }) => (
  <ul className="space-y-2 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-400" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/** Un encadré : ce que le document met en exergue. */
const Encadre = ({ titre, children }: { titre?: string; children: React.ReactNode }) => (
  <div className="rounded-xl border border-primary-100 bg-primary-50 p-4 md:p-5">
    {titre && <h3 className="font-semibold text-primary-900 mb-2">{titre}</h3>}
    <div className="text-gray-700 leading-relaxed space-y-2">{children}</div>
  </div>
);

const SOMMAIRE: { id: string; titre: string }[] = [
  { id: 'responsable', titre: '1. Qui est responsable de vos données ?' },
  { id: 'perimetre', titre: '2. À qui cette politique s’applique-t-elle ?' },
  { id: 'donnees', titre: '3. Quelles données utilisons-nous ?' },
  { id: 'origine', titre: '4. D’où viennent les données ?' },
  { id: 'finalites', titre: '5. Pourquoi utilisons-nous ces données ?' },
  { id: 'sante', titre: '6. Données de santé et d’inclusion' },
  { id: 'images', titre: '7. Photos, vidéos et newsletter' },
  { id: 'partage', titre: '8. Avec qui les données peuvent-elles être partagées ?' },
  { id: 'acces', titre: '9. Qui peut accéder aux données chez Éclosion ?' },
  { id: 'conservation', titre: '10. Combien de temps conservons-nous les données ?' },
  { id: 'droits', titre: '11. Vos droits — et ceux de votre enfant' },
  { id: 'site', titre: '12. Site internet, cookies et candidatures' },
  { id: 'ia', titre: '13. Profilage, décisions automatisées et intelligence artificielle' },
  { id: 'incidents', titre: '14. Incidents, réclamations et mises à jour' },
];

const DUREES: { categorie: string; regle: string }[] = [
  {
    categorie: 'Dossier actif de l’enfant, santé et inclusion',
    regle:
      '1 an après la dernière date de présence, sauf donnée devant être conservée plus longtemps pour une autre raison.',
  },
  {
    categorie: 'Présences et inscriptions nécessaires à la justification',
    regle: '10 ans.',
  },
  { categorie: 'Factures, paiements et attestations fiscales', regle: '10 ans.' },
  {
    categorie: 'Rapports d’incident ou d’accident',
    regle:
      '10 ans après l’incident ; prolongation si un dossier d’assurance, une réclamation ou une procédure reste ouvert.',
  },
  { categorie: 'Photos/vidéos en espaces privés parents', regle: '1 an.' },
  {
    categorie: 'Photothèque institutionnelle sélectionnée',
    regle:
      'Conservation longue tant que l’usage reste pertinent et autorisé ; réexamen tous les 3 ans.',
  },
  {
    categorie: 'Doublons / photos sans usage identifié',
    regle: 'Suppression au plus tard après 5 ans.',
  },
  {
    categorie: 'Compte inactif',
    regle:
      'Suppression après 1 an lorsqu’aucun dossier ne justifie son maintien ; les pièces fiscales et comptables nécessaires restent archivées pendant leur durée légale.',
  },
];

const ConfidentialitePage = () => {
  useEnTete({
    titre: 'Politique de confidentialité | Éclosion ASBL',
    description:
      'Comment Éclosion ASBL collecte, utilise, partage et conserve les données des enfants accueillis et de leurs familles : finalités, bases légales, durées de conservation, droits et contacts.',
    chemin: CHEMIN_POLITIQUE,
  });

  return (
    <div className="bg-white">
      <div className="container max-w-3xl mx-auto px-4 py-10 md:py-16">
        {/* En-tête */}
        <header className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">
            Éclosion ASBL
          </p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900 md:text-4xl">
            Politique de confidentialité
          </h1>
          <p className="mt-3 text-sm text-gray-500">
            Version {VERSION_POLITIQUE} · Dernière mise à jour&nbsp;: {MAJ_POLITIQUE}
          </p>
        </header>

        {/* En bref */}
        <Encadre titre="En bref">
          <p>
            Éclosion utilise les données nécessaires pour inscrire et accueillir les enfants,
            assurer leur sécurité, gérer les présences et la facturation, et communiquer avec
            les familles. Les données de santé sont particulièrement protégées. Nous ne vendons
            pas les données, ne faisons pas de profilage et n’utilisons pas l’intelligence
            artificielle pour analyser les dossiers des enfants.
          </p>
          <p>
            Pour toute question ou demande&nbsp;:{' '}
            <a
              href={`mailto:${COURRIEL_VIE_PRIVEE}`}
              className="font-medium text-primary-700 underline underline-offset-2"
            >
              {COURRIEL_VIE_PRIVEE}
            </a>
            .
          </p>
        </Encadre>

        {/* Sommaire */}
        <nav aria-label="Sommaire" className="mt-10 rounded-xl border border-gray-200 p-4 md:p-5">
          <h2 className="font-semibold text-gray-900 mb-3">Sommaire</h2>
          <ol className="grid gap-1.5 sm:grid-cols-2">
            {SOMMAIRE.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-sm text-gray-600 hover:text-primary-700 hover:underline underline-offset-2"
                >
                  {s.titre}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-10 space-y-10">
          <Section id="responsable" titre="1. Qui est responsable de vos données ?">
            <p>
              Éclosion ASBL est responsable des traitements décrits dans cette politique.
            </p>
            <div className="rounded-lg bg-gray-50 p-4 text-sm">
              <p>Rue des Palais 32-34, 1030 Schaerbeek, Belgique</p>
              <p>N° d’entreprise&nbsp;: 0848.534.719</p>
              <p>
                Téléphone&nbsp;: 0470 470 503 · E-mail&nbsp;:{' '}
                <a href="mailto:info@eclosion.be" className="text-primary-700 underline underline-offset-2">
                  info@eclosion.be
                </a>
              </p>
              <p>
                Contact vie privée&nbsp;:{' '}
                <a
                  href={`mailto:${COURRIEL_VIE_PRIVEE}`}
                  className="text-primary-700 underline underline-offset-2"
                >
                  {COURRIEL_VIE_PRIVEE}
                </a>
              </p>
            </div>
          </Section>

          <Section id="perimetre" titre="2. À qui cette politique s’applique-t-elle ?">
            <p>
              Elle concerne les enfants accueillis par Éclosion (principalement de 2,5 à 16 ans),
              leurs parents et représentants légaux, les personnes autorisées à venir les
              chercher, les travailleurs, bénévoles et stagiaires, les écoles et partenaires, les
              services d’aide à la jeunesse, les organisations utilisatrices de l’application,
              les candidats à un emploi ou un stage et les visiteurs du site.
            </p>
            <p>
              Éclosion organise notamment de l’accueil extrascolaire, des centres de vacances,
              plaines et stages, du soutien scolaire, des activités parascolaires, des journées
              pédagogiques animées, des classes de dépaysement et d’autres activités éducatives
              et socio-éducatives.
            </p>
          </Section>

          <Section id="donnees" titre="3. Quelles données utilisons-nous ?">
            <p>Selon le service concerné, Éclosion peut traiter notamment&nbsp;:</p>
            <Puces
              items={[
                <>
                  <strong>Identité et inscription</strong>&nbsp;: nom, prénom, date de naissance,
                  adresse, école, classe, photo de profil et lien avec le parent ou représentant
                  légal.
                </>,
                <>
                  <strong>Accueil et présences</strong>&nbsp;: activités et stages, dates et
                  heures de présence, absences et retards, autorisation de sortie, personnes
                  autorisées à reprendre l’enfant et contacts d’urgence.
                </>,
                <>
                  <strong>Santé et inclusion</strong>&nbsp;: allergies, médicaments ou
                  traitements, régime alimentaire, informations médicales utiles, médecin,
                  handicap, besoins particuliers, aménagements et consignes nécessaires à un
                  accueil sécurisé.
                </>,
                <>
                  <strong>Suivi éducatif</strong>&nbsp;: observations utiles, besoins
                  d’accompagnement, situations de conflit, rapports d’incident ou d’accident et
                  échanges utiles avec les partenaires éducatifs.
                </>,
                <>
                  <strong>Images et expression</strong>&nbsp;: photos, vidéos, enregistrements
                  audio, créations, témoignages ou paroles de l’enfant lorsque l’utilisation
                  prévue est autorisée.
                </>,
                <>
                  <strong>Données du parent ou de l’organisation</strong>&nbsp;: identité,
                  adresse, téléphone, e-mail, informations de compte, données de facturation et,
                  lorsque nécessaire, numéro de registre national.
                </>,
                <>
                  <strong>Site internet</strong>&nbsp;: données du formulaire de contact, adresse
                  e-mail d’inscription à la newsletter et informations transmises dans le cadre
                  d’une candidature (CV, lettre de motivation, diplômes, coordonnées et autres
                  éléments communiqués).
                </>,
              ]}
            />
            <Encadre titre="Numéro de registre national">
              <p>
                Le numéro de registre national de l’enfant et du parent n’est utilisé que pour les
                attestations fiscales et les obligations qui y sont directement liées.
              </p>
            </Encadre>
          </Section>

          <Section id="origine" titre="4. D’où viennent les données ?">
            <p>
              La plupart des informations sont fournies directement par le parent ou représentant
              légal. Certaines peuvent également provenir de l’école, d’un service d’aide à la
              jeunesse ou d’une autre organisation qui inscrit ou accompagne l’enfant, de
              l’enfant lui-même, des observations des équipes, de Quick School, ou d’un partenaire
              éducatif (école, PMS, PSE, SAJ, etc.) dans le cadre d’un suivi autorisé.
            </p>
            <p>
              Lorsqu’Éclosion reçoit des données d’un tiers, elle limite leur utilisation à ce qui
              est utile à l’accueil, à la sécurité, au suivi ou à l’objectif convenu.
            </p>
          </Section>

          <Section id="finalites" titre="5. Pourquoi utilisons-nous ces données ?">
            <Puces
              items={[
                'Créer et gérer les comptes et les inscriptions, organiser les activités et communiquer les informations pratiques.',
                'Assurer la présence, la sécurité, les arrivées et départs, les contacts d’urgence et la continuité de l’accueil.',
                'Adapter l’accueil aux besoins de santé, d’inclusion et de sécurité de l’enfant.',
                'Assurer le suivi éducatif, gérer les incidents et coopérer avec les familles et partenaires lorsque cela est pertinent et autorisé.',
                'Gérer la facturation, les paiements, les attestations fiscales, les contrôles, agréments, subventions et coorganisations.',
                'Répondre aux demandes reçues via le site, gérer les candidatures et, avec consentement, envoyer la newsletter.',
                'Utiliser des photos ou vidéos sur les supports autorisés par le parent et, lorsque cela est pertinent, en tenant compte de la volonté de l’enfant.',
              ]}
            />
            <Encadre titre="Bases juridiques">
              <p>
                Selon le traitement, Éclosion s’appuie sur l’exécution du service ou les démarches
                préalables à l’inscription, le respect d’obligations légales ou réglementaires,
                ses intérêts légitimes à organiser et sécuriser ses activités, ou le consentement
                lorsque celui-ci est requis.
              </p>
              <p>
                Les données de santé et autres données sensibles sont traitées uniquement dans les
                conditions particulières prévues par le RGPD, pour les besoins de sécurité et
                d’accueil et, en situation d’urgence, lorsque la protection d’intérêts vitaux
                l’exige.
              </p>
            </Encadre>
          </Section>

          <Section id="sante" titre="6. Données de santé et d’inclusion">
            <p>
              Une fiche santé est demandée pour chaque enfant accueilli. Elle sert à permettre aux
              équipes de connaître les informations nécessaires à la sécurité et à l’adaptation de
              l’accueil. Lors de chaque nouvelle inscription, le parent doit vérifier les
              informations, supprimer celles qui ne sont plus valables et ajouter les nouvelles
              informations utiles.
            </p>
            <p>
              Lorsqu’une information est indispensable pour accueillir l’enfant en sécurité et
              qu’elle n’est pas communiquée, Éclosion peut ne pas être en mesure d’accepter ou de
              poursuivre l’accueil. L’objectif est d’évaluer les besoins concrets de l’enfant et
              les moyens nécessaires, pas de prendre une décision sur la seule base d’un
              diagnostic.
            </p>
          </Section>

          <Section id="images" titre="7. Photos, vidéos et newsletter">
            <p>
              Les autorisations concernant les images sont séparées. Le parent peut accepter ou
              refuser indépendamment l’utilisation dans&nbsp;:
            </p>
            <Puces
              items={[
                'l’espace privé destiné aux familles ;',
                'les rapports d’activité et projets d’accueil publics ;',
                'le site internet public ;',
                'les réseaux sociaux d’Éclosion ;',
                'les affiches, flyers et brochures.',
              ]}
            />
            <p>
              Pour les réseaux sociaux, Éclosion utilise uniquement des images autorisées sur
              lesquelles l’enfant apparaît de dos ou n’est pas facilement reconnaissable. Un
              enfant ou jeune suffisamment capable de comprendre la situation peut lui-même
              demander qu’une photo ou vidéo de lui ne soit plus utilisée.
            </p>
            <p>
              L’inscription à la newsletter est facultative, distincte de l’inscription aux
              activités et non cochée par défaut. Elle peut contenir des informations pratiques,
              actualités, invitations, promotions et appels aux dons. Le consentement peut être
              retiré à tout moment.
            </p>
          </Section>

          <Section id="partage" titre="8. Avec qui les données peuvent-elles être partagées ?">
            <p>
              Éclosion ne vend ni ne loue les données personnelles. Elles peuvent être
              communiquées uniquement lorsque cela est nécessaire à l’activité, à une obligation,
              à la sécurité, au suivi, à la facturation ou au recouvrement.
            </p>
            <Puces
              items={[
                <>
                  <strong>ONE et communes partenaires</strong>&nbsp;: nom, prénom, date de
                  naissance, âge, numéro de registre national et nombre de présences lorsque ces
                  informations sont requises dans le cadre de l’agrément, du subventionnement ou
                  de la coorganisation.
                </>,
                <>
                  <strong>Autres organismes subsidiants</strong>&nbsp;: uniquement les données
                  nécessaires si un futur dispositif de financement l’exige.
                </>,
                <>
                  <strong>Ethias</strong>&nbsp;: identité de l’enfant, coordonnées des parents et
                  description de l’accident lorsqu’un dossier d’assurance doit être ouvert.
                </>,
                <>
                  <strong>Écoles, PMS, PSE, services d’aide à la jeunesse et autres partenaires
                  éducatifs</strong>&nbsp;: informations utiles au suivi, lorsque la situation le
                  justifie et avec l’accord approprié du parent et/ou de l’enfant.
                </>,
                <>
                  <strong>EURO FIDES Credit Management SA</strong>&nbsp;: données nécessaires au
                  recouvrement d’une facture impayée, notamment identité du débiteur et de
                  l’enfant, école, coordonnées, facture, montant, échéance, rappels, paiements et
                  pièces justificatives. Aucune donnée de santé ou de suivi éducatif n’est
                  transmise à cette fin.
                </>,
                <>
                  <strong>Quick School / FLAG 2000</strong>&nbsp;: nom et prénom de l’enfant,
                  classe, présences, heure de sortie, coordonnées du parent, données de
                  facturation et de paiement, ainsi que les numéros de registre national
                  nécessaires aux attestations fiscales.
                </>,
              ]}
            />
            <Encadre titre="Prestataires techniques">
              <p>
                Éclosion utilise notamment OVHcloud (site public), Netlify (hébergement de
                l’application), Supabase (base de données, authentification, stockage et
                sauvegardes), Google Workspace (Gmail, Drive, Sheets et envoi d’e-mails), Stripe
                (paiements) et Quick School. Les données principales de l’application sont
                hébergées par Supabase dans la région de Francfort, en Allemagne.
              </p>
              <p>
                Certains prestataires internationaux peuvent traiter ponctuellement des données en
                dehors de l’Espace économique européen. Lorsque cela se produit, Éclosion veille à
                l’existence d’un mécanisme de transfert reconnu par le RGPD, tel qu’une décision
                d’adéquation ou des clauses contractuelles types.
              </p>
            </Encadre>
          </Section>

          <Section id="acces" titre="9. Qui peut accéder aux données chez Éclosion ?">
            <p>
              L’accès est limité aux personnes autorisées selon leur fonction. Les parents et
              organisations n’accèdent qu’aux informations liées à leur compte et aux enfants
              qu’ils sont autorisés à gérer.
            </p>
            <p>
              Les animateur·trices peuvent être mobiles entre différents centres. Pour garantir la
              sécurité lors d’un remplacement ou changement d’affectation, ils et elles peuvent
              consulter les informations opérationnelles et de santé nécessaires concernant les
              enfants actuellement accueillis par Éclosion. Les coordinateur·trices disposent de
              droits plus étendus pour organiser et mettre à jour l’accueil. Les
              administrateur·trices disposent des droits nécessaires à l’administration générale.
            </p>
            <p>
              Les données purement fiscales, comptables ou administratives sont séparées autant
              que possible des informations nécessaires au terrain.
            </p>
            <Encadre titre="Mesures de sécurité">
              <p>
                Éclosion met en place des contrôles d’accès, des comptes autorisés, une
                authentification renforcée sur certains services, une déconnexion automatique
                après inactivité, des sauvegardes, une limitation des téléchargements et une
                désactivation des accès lorsqu’un travailleur quitte l’organisation. Les fiches
                santé imprimées sont conservées dans le bureau de coordination pendant l’activité
                puis détruites par déchiquetage lorsqu’elles ne sont plus nécessaires.
              </p>
            </Encadre>
          </Section>

          <Section id="conservation" titre="10. Combien de temps conservons-nous les données ?">
            {/* Le tableau déborde sur un téléphone : il scrolle seul, la page non. */}
            <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
              <table className="w-full min-w-[34rem] border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-left">
                    <th className="py-2 pr-4 font-semibold text-gray-900">Catégorie</th>
                    <th className="py-2 font-semibold text-gray-900">Durée / règle</th>
                  </tr>
                </thead>
                <tbody>
                  {DUREES.map((d) => (
                    <tr key={d.categorie} className="border-b border-gray-100 align-top">
                      <td className="py-3 pr-4 font-medium text-gray-800">{d.categorie}</td>
                      <td className="py-3 text-gray-700">{d.regle}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="droits" titre="11. Vos droits — et ceux de votre enfant">
            <p>
              Vous pouvez demander l’accès aux données, leur rectification, leur effacement
              lorsque celui-ci est possible, leur limitation, vous opposer à certains traitements
              et demander la portabilité lorsque les conditions légales sont réunies. Lorsque le
              traitement repose sur votre consentement, vous pouvez le retirer à tout moment.
            </p>
            <p>
              Les demandes sont adressées à{' '}
              <a
                href={`mailto:${COURRIEL_VIE_PRIVEE}`}
                className="font-medium text-primary-700 underline underline-offset-2"
              >
                {COURRIEL_VIE_PRIVEE}
              </a>
              , de préférence depuis l’adresse e-mail liée au compte. En cas de doute raisonnable,
              Éclosion peut demander des informations complémentaires pour vérifier l’identité
              sans exiger systématiquement une copie de carte d’identité.
            </p>
            <p>
              Certaines données ne peuvent pas être supprimées immédiatement lorsqu’Éclosion doit
              les conserver pour une obligation fiscale ou comptable, un dossier d’assurance, une
              procédure ou la défense de ses droits.
            </p>
            <Encadre titre="Les droits de l’enfant">
              <p>
                Un enfant ou jeune suffisamment capable de comprendre sa demande peut exercer
                directement ses droits&nbsp;: demander quelles données sont conservées à son
                sujet, faire corriger ou supprimer une information lorsque c’est possible,
                demander qu’une photo ou vidéo ne soit plus utilisée, ou demander des explications
                sur une observation inscrite dans son dossier.
              </p>
              <p>
                Lorsqu’un jeune demande qu’une information personnelle ne soit pas transmise à son
                parent, Éclosion tient compte de sa demande, de son âge, de sa maturité et de la
                nature de l’information. Cette confidentialité peut être levée lorsque cela est
                nécessaire pour protéger l’enfant ou une autre personne, notamment en cas de mise
                en danger.
              </p>
            </Encadre>
          </Section>

          <Section id="site" titre="12. Site internet, cookies et candidatures">
            <p>
              Le formulaire de contact peut recueillir le nom, prénom, e-mail, téléphone et
              message afin de répondre à la demande. Les candidatures peuvent contenir un CV, une
              lettre de motivation, des diplômes, des coordonnées et d’autres informations
              communiquées volontairement. Ces données sont conservées pendant le temps nécessaire
              au traitement et au suivi de la demande, sous réserve des obligations légales
              applicables.
            </p>
            <p>
              Éclosion n’utilise actuellement ni Google Analytics ni outil publicitaire de suivi
              sur son site. Le site peut contenir de simples liens vers les réseaux sociaux. Des
              mécanismes techniques strictement nécessaires au fonctionnement, à la sécurité ou à
              l’authentification peuvent toutefois être utilisés par les services techniques.
            </p>
          </Section>

          <Section
            id="ia"
            titre="13. Profilage, décisions automatisées et intelligence artificielle"
          >
            <p>
              Éclosion ne réalise pas de profilage des enfants ou des familles et ne prend pas de
              décision produisant un effet significatif sur la seule base d’un traitement
              automatisé. Les données réelles des fiches santé, dossiers d’inclusion, observations
              éducatives ou rapports d’incident ne sont pas analysées par une intelligence
              artificielle.
            </p>
          </Section>

          <Section id="incidents" titre="14. Incidents, réclamations et mises à jour">
            <p>
              Tout incident de sécurité impliquant des données personnelles peut être signalé à{' '}
              <a
                href={`mailto:${COURRIEL_VIE_PRIVEE}`}
                className="font-medium text-primary-700 underline underline-offset-2"
              >
                {COURRIEL_VIE_PRIVEE}
              </a>
              . Lorsque la réglementation l’exige, Éclosion notifie la violation à l’Autorité de
              protection des données et informe les personnes concernées en cas de risque élevé.
            </p>
            <p>
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez contacter Éclosion
              à la même adresse. Vous conservez également le droit d’introduire une réclamation
              auprès de l’Autorité de protection des données (APD) en Belgique.
            </p>
            <p>
              Cette politique peut évoluer. Son numéro de version et sa date de dernière mise à
              jour sont indiqués en tête de page. Les modifications importantes font l’objet d’une
              information directe des personnes concernées. Les versions précédentes sont
              archivées par Éclosion.
            </p>
          </Section>
        </div>

        {/* Pied de page du document */}
        <div className="mt-12 rounded-xl border border-gray-200 bg-gray-50 p-5 md:p-6">
          <h2 className="font-semibold text-gray-900 mb-2">Une question sur vos données ?</h2>
          <p className="text-gray-700 leading-relaxed">
            Écrivez-nous à{' '}
            <a
              href={`mailto:${COURRIEL_VIE_PRIVEE}`}
              className="font-medium text-primary-700 underline underline-offset-2"
            >
              {COURRIEL_VIE_PRIVEE}
            </a>
            . Nous répondons aux demandes relatives aux droits des personnes dans les délais prévus
            par le RGPD, en principe dans un délai d’un mois.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={`mailto:${COURRIEL_VIE_PRIVEE}`}
              className="btn-primary inline-flex items-center justify-center"
            >
              <Mail className="mr-2 h-4 w-4" />
              Nous écrire
            </a>
            <a
              href={DOCUMENT_SOURCE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center text-sm text-gray-600 hover:text-primary-700"
            >
              Consulter le document source
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        <p className="mt-8 text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-700 underline underline-offset-2">
            Retour à l’accueil
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ConfidentialitePage;
